/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import type { Element, ElementType } from 'react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { get } from 'lodash';
import { ESCAPE } from 'Utils/keyCodes';
import { themes } from './DropDown.themes';
type DropDownProps = {
  children: (handler: () => void, isExpanded: boolean) => Element<any> | null | undefined;
  className?: string;
  contentStyle?: CSSRuleList;
  contentClassName?: string;
  hasCloseOnClick?: boolean;
  hasArrow?: boolean;
  isExpanded?: boolean;
  noLayout?: boolean;
  renderTrigger?: (handler: () => void, isExpanded: boolean) => Element<any>;
  rootRef?: {
    current: React$ElementRef<ElementType> | null | undefined;
  };
  theme?: keyof typeof themes;
  onClose?: () => void;
  unmountsOnClose?: boolean;
};
const defaultProps = {
  hasArrow: true,
  isExpanded: false,
  noLayout: false,
  theme: 'default' as keyof typeof themes,
  unmountsOnClose: false,
};
export function DropDown(rawProps: DropDownProps) {
  const props = { ...defaultProps, ...rawProps };
  const {
    children,
    className,
    contentStyle,
    contentClassName,
    hasArrow,
    hasCloseOnClick,
    noLayout,
    renderTrigger,
    theme,
    onClose,
    unmountsOnClose,
  } = props;

  const [isExpanded, setIsExpanded] = useState(props.isExpanded || false);
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isExpandedRef = useRef(isExpanded);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  const collapse = useCallback(() => {
    if (onClose) {
      onClose();
    }

    if (!unmountsOnClose) {
      setIsExpanded(false);
    }
  }, [onClose, unmountsOnClose]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === ESCAPE) {
        collapse();
      }
    },
    [collapse],
  );

  const onClickOutside = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (
        !contentRef.current || // @ts-expect-error TODO(flow-migration)
        contentRef.current.contains(e.target) || // @ts-expect-error TODO(flow-migration)
        rootRef.current.contains(e.target) ||
        (get(props, 'rootRef.current') && props.rootRef?.current.contains(e.target)) ||
        (e.button && e.button !== 0)
      ) {
        return;
      }

      collapse();
    },
    [collapse, props.rootRef],
  );

  useEffect(() => {
    function addEventListeners() {
      if (typeof window !== 'undefined') {
        document.addEventListener('mouseup', onClickOutside, false);
        document.addEventListener('touchstart', onClickOutside, false);
        document.addEventListener('keydown', onKeyDown, false);
      }
    }

    function removeEventListeners() {
      if (typeof window !== 'undefined') {
        document.removeEventListener('mouseup', onClickOutside, false);
        document.removeEventListener('touchstart', onClickOutside, false);
        document.removeEventListener('keydown', onKeyDown, false);
      }
    }

    if (isExpanded) {
      addEventListeners();
    } else {
      removeEventListeners();
    }

    return removeEventListeners;
  }, [isExpanded, onClickOutside, onKeyDown]);

  useEffect(() => {
    if (props.isExpanded !== isExpandedRef.current) {
      setIsExpanded(props.isExpanded || false);
    }
  }, [props.isExpanded]);

  const toggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const { thBody } = themes({
    hasArrow,
  })[theme];
  return (
    <div className={classnames('inline-block', className)} ref={rootRef}>
      {renderTrigger && renderTrigger(toggle, isExpanded)}
      {isExpanded && (
        <div
          className={classnames(
            {
              'absolute left-1/2 w-full min-w-full -translate-x-1/2 transform': !noLayout,
            },
            contentClassName,
            thBody,
          )}
          data-testid="DropDown-body"
          onClick={hasCloseOnClick ? collapse : null}
          ref={contentRef}
          style={contentStyle}
        >
          {children(toggle, isExpanded)}
        </div>
      )}
    </div>
  );
}
